import { LitElement, html, css, animationHelpers } from '@hh/global.js'

class ScrollText extends LitElement {
  static properties = {
    characterSplit: {
      type: 'String',
    },
    slide: {
      type: 'Number',
    },
    opacityStart: {
      type: 'Number',
    },
    opacityEnd: {
      type: 'Number',
    },
    transition: {
      type: 'Number',
    },
    reverse: {
      type: 'Number',
    },
    scrollTarget: {
      type: 'String'
    }
  }

  constructor() {
    super()
    this.originalInnerText = this.innerText
  }

  connectedCallback() {
    super.connectedCallback()

    this.scrollTargetElm = document.querySelector(this.scrollTarget) ?? this

    const customProperties = {
      '--slide': `${this.slide}px`,
      '--opacityStart': `${this.opacityStart ?? 0.25}`,
      '--opacityEnd': `${this.opacityEnd ?? 1}`,
      '--transition': `${this.transition ?? 500}ms`,
    }

    Object.keys(customProperties).forEach((key) => {
      this.style.setProperty(key, customProperties[key])
    })
  }

  disconnectedCallback() {
    super.disconnectedCallback()
    window.removeEventListener('scroll', this.scrollCheck.bind(this))
  }

  firstUpdated() {
    super.firstUpdated()
    this.segments = [...this.shadowRoot.querySelectorAll('.js-segment')]
    this.lastScrollPosition = window.scrollY || document.documentElement.scrollTop

    this.scrollCheck()

    animationHelpers.createIntersectionObserver(
      this.scrollTargetElm,
      (inView) => {
        if (inView) {
          window.addEventListener('scroll', this.scrollCheck.bind(this))
        } else {
          window.removeEventListener('scroll', this.scrollCheck.bind(this))
        }
      },
      {
        onlyCallbackOnIntersection: false,
        disableAfterIntersection: false,
        threshold: 0
      }
    )
  }

  isScrollingDown(pageScrollPosition) {
    const isDown = this.lastScrollPosition < pageScrollPosition
    this.lastScrollPosition = pageScrollPosition
    return isDown
  }

  scrollCheck() {
    if (!this.segments) {
      return
    }

    const pageScrollPosition = window.scrollY || document.documentElement.scrollTop
    const scrollPercentage = animationHelpers.scrollThroughPercentage(this.scrollTargetElm, 0.8)
    const isScrollingDown = this.isScrollingDown(pageScrollPosition)

    if (!isScrollingDown && Number(this.reverse) === 0) {
      return
    }

    this.segments
      .filter((_, index) => {
        const segmentPercentage = (index / this.segments.length) * 100
        return isScrollingDown
          ? segmentPercentage <= scrollPercentage
          : segmentPercentage > scrollPercentage
      })
      .forEach((segment, index) => {
        isScrollingDown
          ? segment.classList.add('scrolled')
          : segment.classList.remove('scrolled')
      })
  }

  segmentClasses(spanType) {
    const spanTypeToUse = ['letter', 'word', 'sentence', 'none'].includes(
      this.characterSplit
    )
      ? this.characterSplit
      : 'word'

    const segmentClass = spanType === spanTypeToUse ? 'segment js-segment' : ''

    const slideClass =
      !Number.isNaN(Number(this.slide)) && Number(this.slide) !== 0
        ? 'has-slide'
        : ''

    return [segmentClass, slideClass].join(' ')
  }

  createSegments(string) {
    const wordToLetter = (word) =>
      word
        .split('')
        .map(
          (letter) =>
            html`<span
              class="letter ${this.segmentClasses('letter')} ${letter.length}"
              >${letter === ' ' ? html`&nbsp;` : letter}</span
            >`
        )

    const sentenceToWord = (sentence) =>
      sentence
        .match(/\S+\s*/g)
        .map(
          (word) =>
            html`<span class="word ${this.segmentClasses('word')}"
              >${wordToLetter(word)}</span
            >`
        )

    const stringToSentence = (string) =>
      string
        .match(/.*?\.\s*|\S+\s*/g)
        .map(
          (sentence) =>
            html`<span class="sentence ${this.segmentClasses('sentence')}"
              >${sentenceToWord(sentence)}</span
            >`
        )

    return stringToSentence(string)
  }

  render() {
    return this.createSegments(this.originalInnerText)
  }

  static styles = css`
    :host {
      display: block;
      height: auto;
      position: relative;
      width: 100%;
      margin: 0;
      padding: 0;
    }

    .letter,
    .sentence {
      display: inline;
    }

    .sentence {
      overflow-wrap: break-word;
    }

    .word {
      display: inline-block;
    }

    .segment {
      position: relative;
      opacity: var(--opacityStart, 0.25);
      transition: all var(--transition, 500ms) ease;
      transform: translateY(var(--slide, 0));
    }

    .segment.has-slide {
      display: inline-block;
    }

    .sentence.has-slide {
      overflow-wrap: anywhere;
    }

    .segment.scrolled {
      opacity: var(--opacityEnd, 1);
      transform: translateY(0);
    }
  `
}

customElements.define('scroll-text', ScrollText)
